import type { ComponentType } from '@/types/schemas/workspace'
import type { LinkMode, PanelState, WindowConfig } from '../types'
import { holdsDayWhenLocked } from './focus-date-sources'

/**
 * Panel ids that should receive a broadcast from `sourceId`. Mirrors the
 * link-mode logic in `handleSymbolChange`: `none` → empty; `onChange` →
 * onChange peers; color → same-color peers plus all onChange peers.
 */
export function getBroadcastTargetIds(
  configs: Record<string, WindowConfig>,
  sourceId: string,
): string[] {
  const sourceCfg = configs[sourceId]
  if (!sourceCfg) return []
  const sourceMode: LinkMode = sourceCfg.linkMode ?? 'onChange'
  if (sourceMode === 'none') return []

  const targets: string[] = []
  for (const [id, cfg] of Object.entries(configs)) {
    if (id === sourceId) continue
    const targetMode: LinkMode = cfg.linkMode ?? 'onChange'
    const shouldUpdate =
      sourceMode === 'onChange'
        ? targetMode === 'onChange'
        : targetMode === sourceMode || targetMode === 'onChange'
    if (shouldUpdate) targets.push(id)
  }
  return targets
}

/**
 * The `symbolIndex` a NEWLY CREATED panel should be seeded with.
 *
 * A new panel carries no `linkMode`, which everywhere means `'onChange'` — so
 * at creation time it is joining the onChange group and must open on that
 * group's CURRENT ticker, not `symbols[0]` ('AAPL', the old hardcoded seed the
 * user read as "every new chart loads Apple"). Preference order:
 *   1. an existing onChange-group panel (broadcasts keep the group
 *      index-synced, so any member holds the group's current ticker);
 *   2. else any color-linked panel — an all-colored workspace still has a
 *      ticker on screen, and opening there beats the AAPL default;
 *   3. never a `'none'` panel — isolation is respected in both directions;
 *   4. else 0 (empty workspace: the first panel keeps the default symbol).
 */
export function seedSymbolIndex(configs: Record<string, WindowConfig>): number {
  let colorFallback: number | null = null
  for (const cfg of Object.values(configs)) {
    if (typeof cfg.symbolIndex !== 'number') continue
    const mode: LinkMode = cfg.linkMode ?? 'onChange'
    if (mode === 'onChange') return cfg.symbolIndex
    if (mode !== 'none' && colorFallback == null) colorFallback = cfg.symbolIndex
  }
  return colorFallback ?? 0
}

/**
 * Date-broadcast effect for a scanner row click: sets the shared focusDate
 * + source, and clears panel-local focusDate on every linked target so the
 * broadcast wins over a stale local override. Source panel keeps its own
 * local. Setters are injected so this stays unit-testable without React.
 */
export function applyDateBroadcast(
  configs: Record<string, WindowConfig>,
  sourceId: string,
  date: string | null,
  setters: {
    setFocusDate: (d: string | null) => void
    setFocusDateSource: (s: ComponentType | null) => void
    updatePanel: (id: string, partial: Partial<PanelState>) => void
  },
): void {
  const sourceCfg = configs[sourceId]
  setters.setFocusDate(date)
  setters.setFocusDateSource(date ? (sourceCfg?.componentType ?? null) : null)
  for (const targetId of getBroadcastTargetIds(configs, sourceId)) {
    setters.updatePanel(targetId, { focusDate: null } as Partial<PanelState>)
  }
}

/**
 * Ticker-clear effect for a selection that is gone (Records listed no
 * records): the source and every panel its broadcast reaches drop their ticker
 * (`tickerCleared`, each panel's empty state) until the next selection, which
 * re-engages them (`handleSymbolChange`). A locked panel is never reached, so
 * it keeps its own. Returns `configs` itself when nothing changes.
 */
export function clearLinkedTickers(
  configs: Record<string, WindowConfig>,
  sourceId: string,
): Record<string, WindowConfig> {
  const ids = [sourceId, ...getBroadcastTargetIds(configs, sourceId)]
  const toClear = ids.filter((id) => configs[id] && !configs[id].tickerCleared)
  if (toClear.length === 0) return configs
  const next = { ...configs }
  for (const id of toClear) next[id] = { ...configs[id], tickerCleared: true }
  return next
}

/**
 * Lock effect for the header padlock. A chart that holds its day while locked
 * stops following the date broadcast (see `holdsDayWhenLocked`), so the day it
 * shows through the broadcast becomes its panel-local focusDate first, and
 * locking never moves it. The pinned day is noted (`lockPinnedDate`) so that
 * unlocking can let it go (`rejoinOnUnlock`). A chart already on a day of
 * its own, or showing none, keeps what it has. Same injected-setter shape as
 * `applyDateBroadcast`.
 */
export function pinDayOnLock(
  configs: Record<string, WindowConfig>,
  panels: Record<string, PanelState | null | undefined>,
  panelId: string,
  mode: LinkMode,
  focusDate: string | null,
  updatePanel: (id: string, partial: Partial<PanelState>) => void,
): void {
  const cfg = configs[panelId]
  if (!cfg || !focusDate || !holdsDayWhenLocked({ ...cfg, linkMode: mode })) return
  if ((panels[panelId] as { focusDate?: string | null } | null | undefined)?.focusDate) return
  updatePanel(panelId, { focusDate, lockPinnedDate: focusDate })
}

/** What unlocking does to a chart the lock pinned (see `rejoinOnUnlock`). */
export interface UnlockRejoin {
  /** The link group's ticker, which the chart takes in the same write as its new link mode. */
  symbolIndex?: number
  /** The chart's panel-state change. */
  panel: Partial<PanelState>
}

/**
 * Unlock effect, the other half of `pinDayOnLock`: the chart rejoins the
 * selection at once, ticker and day together, so it never shows one record's
 * ticker on another record's day. It takes the ticker its new link group shows
 * (a member's `symbolIndex`; record clicks keep a group's members on one
 * ticker, the same source `seedSymbolIndex` reads) and lets go of the pinned
 * day, so the broadcast day shows again. With no other member to take a ticker
 * from, both stay until the next record click moves them together. A day the
 * trader chose while locked (its own calendar) is theirs and stays, with the
 * chart's own ticker. Null when there is nothing to undo.
 */
export function rejoinOnUnlock(
  configs: Record<string, WindowConfig>,
  panels: Record<string, PanelState | null | undefined>,
  panelId: string,
  mode: LinkMode,
): UnlockRejoin | null {
  if (mode === 'none') return null
  const panel = panels[panelId] as
    | { focusDate?: string | null; lockPinnedDate?: string | null }
    | null
    | undefined
  const pinned = panel?.lockPinnedDate
  if (!pinned) return null
  const dropNote: Partial<PanelState> = { lockPinnedDate: undefined }
  if (panel?.focusDate !== pinned) return { panel: dropNote }
  const member = Object.entries(configs).find(
    ([id, cfg]) =>
      id !== panelId &&
      typeof cfg.symbolIndex === 'number' &&
      (cfg.linkMode ?? 'onChange') === mode,
  )
  if (!member) return { panel: dropNote }
  return { symbolIndex: member[1].symbolIndex, panel: { ...dropNote, focusDate: null } }
}
